(function () {
  const rootUrl = window.Shopify?.routes?.root || '/';
  const language = document.documentElement.lang || 'en';
  const requests = new Map();
  const profiles = new Map();
  const tones = new Set(['beige', 'cream', 'brown', 'neutral', 'sand', 'taupe']);
  const tags = p => (p.tags || []).map(t => t.toLowerCase());
  const values = (p, prefix) => tags(p).filter(t => t.startsWith(prefix)).map(t => t.slice(prefix.length));
  const shared = (a, b) => a.filter(v => b.includes(v)).length;
  const key = p => p.handle.replace(/-(?:art-print|poster|print)(?:-\d+)?$/, '');
  const image = p => { const im = p.image || p.featured_image; return typeof im === 'string' ? im : im?.src || im?.url; };
  function load(url) {
    if (!requests.has(url)) requests.set(url, fetch(url).then(r => {
      if (!r.ok) throw new Error('Unavailable product');
      return r.json();
    }));
    return requests.get(url);
  }
  function eligible(p) {
    return p.available !== false && !!image(p) && !tags(p).some(t =>
      t.startsWith('hidden-') || ['pv-custom', 'pv-retired', 'hidden-duplicate'].includes(t) ||
      t.includes('canvas') || t.includes('white border') || (t === 'market:se' && !language.startsWith('sv'))
    ) && !/\bcanvas\b|white[- ]border/i.test(p.handle);
  }
  function metadata(p) {
    const colours = values(p, 'colour:');
    const text = `${p.handle} ${p.title}`.toLowerCase();
    const mono = colours.some(c => c === 'black-white' || c === 'monochrome') ||
      /black-and-white|monochrome|grayscale|greyscale|svartvit/.test(text);
    const subjects = values(p, 'subject:');
    const styles = values(p, 'style:');
    const typography = subjects.includes('typography') || styles.includes('typography') ||
      tags(p).includes('typography & quotes') || /(?:^|-)(?:quote|typography|affirmation|slogan)(?:-|$)/.test(p.handle);
    const family = /nursery|kids-room|children/.test(tags(p).join(' ')) ? 'children' :
      /horror|macabre|midnight-matinee/.test(tags(p).join(' ')) ? 'horror' : 'general';
    return { mono, known: mono || colours.length > 0, colours, styles, subjects, rooms: values(p, 'room:'), typography, family };

  }
  function affinity(a, b) {
    const x = metadata(a), y = metadata(b);
    return Math.min(2, shared(x.styles.filter(s => s !== 'typography'), y.styles)) * 6 +
      Math.min(2, shared(x.subjects.filter(s => s !== 'typography'), y.subjects)) * 3 +
      Math.min(2, shared(x.rooms, y.rooms)) + Math.min(2, shared(x.colours, y.colours)) * 5;

  }
  function palette(p) {
    if (profiles.has(p.handle)) return profiles.get(p.handle);
    const promise = new Promise(resolve => {
      const fallback = metadata(p), im = new Image();
      im.crossOrigin = 'anonymous';
      const timeout = setTimeout(() => resolve(fallback), 5000);
      im.onerror = () => { clearTimeout(timeout); resolve(fallback); };
      im.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas'); canvas.width = canvas.height = 48;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(im, im.width * .12, im.height * .12, im.width * .76, im.height * .76, 0, 0, 48, 48);
          const pixels = ctx.getImageData(0, 0, 48, 48).data, hues = new Array(12).fill(0);
          let n = 0, colourful = 0, saturation = 0, light = 0;
          for (let i = 0; i < pixels.length; i += 4) {
            if (pixels[i + 3] < 128) continue;
            const r = pixels[i] / 255, g = pixels[i + 1] / 255, b = pixels[i + 2] / 255;
            const hi = Math.max(r, g, b), lo = Math.min(r, g, b), delta = hi - lo;
            const sat = hi ? delta / hi : 0;
            n++; saturation += sat; light += (hi + lo) / 2;
            if (sat > .18 && delta > .07) {
              colourful++;
              let hue = hi === r ? ((g - b) / delta + 6) % 6 : hi === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
              hues[Math.floor(hue * 2) % 12]++;
            }
          }
          if (!n) { resolve(fallback); return; }
          resolve({ ...fallback, known: true, mono: colourful / n < .07 && saturation / n < .09,
            saturation: saturation / n, light: light / n, hues: hues.map(v => v / Math.max(1, colourful)) });
        } catch (_) { resolve(fallback); }
      };
      const src = new URL(image(p), location.origin); src.searchParams.set('width', '96'); im.src = src.href;
    });
    profiles.set(p.handle, promise); return promise;
  }
  function harmony(a, b) {
    if (!a.known || !b.known) return -20;
    if (a.mono && b.mono) return 30;
    if (a.mono || b.mono) return 8;
    if (a.hues && b.hues) {
      const overlap = a.hues.reduce((sum, v, i) => sum + Math.min(v, b.hues[i]), 0);
      return overlap * 30 - Math.abs(a.saturation - b.saturation) * 12;
    }
    const colourMatch = shared(a.colours, b.colours);
    const soft = a.colours.some(c => tones.has(c)) && b.colours.some(c => tones.has(c));
    return colourMatch ? 20 : soft ? 12 : -10;
  }
  function normalize(p) {
    return { ...p, variants: p.variants.map(v => {
      const match = String(v.option1).replace(',', '.').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/);
      const size = match && /cm/i.test(v.option1) ? `${Number(match[1])}x${Number(match[2])} cm` : v.option1;
      return { ...v, option1: size, option2: v.option2 || 'No frame' };
    }) };
  }
  function combinations(p) {
    return new Set((p.variants || []).filter(v => v.available && v.option1 === '50x70 cm').map(v => `${v.option1}|${v.option2 || ''}`));
  }
  function common(all) {
    const sets = all.map(combinations);
    return [...sets[0]].some(k => sets.every(s => s.has(k)));
  }
  function balanced(items, limit, productOf) {
    const text = items.filter(item => metadata(productOf(item)).typography);
    const art = items.filter(item => !metadata(productOf(item)).typography);
    const chosen = [...art.slice(0, Math.ceil(limit * .7)), ...text.slice(0, Math.floor(limit * .3))];
    for (const item of items) if (chosen.length < limit && !chosen.includes(item)) chosen.push(item);
    return chosen;
  }
  function compatible(a, b) {
    if (!a.known || !b.known || a.family !== b.family) return false;
    if (Number.isFinite(a.saturation) && Number.isFinite(b.saturation)) {
      if (Math.abs(a.saturation - b.saturation) > .38) return false;
      if (Math.abs(a.light - b.light) > .48) return false;
    }
    if (a.mono || b.mono) return true;
    return harmony(a, b) >= 12;
  }
  function select(me, candidates) {
    let winner = [], best = -Infinity;
    for (let i = 0; i < candidates.length; i++) for (let j = i + 1; j < candidates.length; j++) {
      const a = candidates[i], b = candidates[j];
      if (key(a.p) === key(b.p) || image(a.p) === image(b.p)) continue;
      const trio = [me, a, b];
      const textCount = trio.filter(item => metadata(item.p).typography).length;
      if (textCount > 1) continue;
      if (me.visual.mono && trio.filter(item => !item.visual.mono).length > 1) continue;
      if (!common(trio.map(item => item.p))) continue;
      if (!compatible(me.visual, a.visual) || !compatible(me.visual, b.visual) || !compatible(a.visual, b.visual)) continue;
      const art = trio.filter(item => !metadata(item.p).typography);
      if (art.length < 2) continue;
      const coherentArt = art.every((item, n) => art.slice(n + 1).every(other =>
        shared(item.visual.styles, other.visual.styles) > 0 ||
        shared(item.visual.subjects, other.visual.subjects) > 0));
      if (!coherentArt) continue;
      const pairScores = [[me, a], [me, b], [a, b]].map(([x, y]) =>
        harmony(x.visual, y.visual) * 2 + affinity(x.p, y.p));
      const score = pairScores.reduce((sum, value) => sum + value, 0) + Math.min(...pairScores) + (textCount === 1 ? 12 : 0);
      if (score > best) { best = score; winner = [a.p, b.p]; }
    }
    return winner;
  }
  function roomGroup(visuals) {
    const measured = visuals.filter(v => Number.isFinite(v.light));
    const average = measured.length ? measured.reduce((sum, v) => sum + v.light, 0) / measured.length : null;
    const pale = measured.filter(v => v.light >= .62).length;
    if (pale >= 2 || (average !== null && average >= .62) ||
      (!measured.length && visuals.some(v => v.colours.some(c => ['white', 'cream', 'beige', 'black-white'].includes(c))))) return 'light';
    if (average !== null && average < .42 && pale === 0) return 'dark';
    return 'soft';
  }
  function scene(root, products, visuals) {
    const host = root.querySelector('[data-pv-look-room]');
    if (!host) return;
    const group = roomGroup(visuals);
    const rooms = (host.dataset[group + 'Rooms'] || host.dataset.rooms).split(',').filter(Boolean);
    const background = host.querySelector('[data-pv-look-room-background]');
    const prints = host.querySelector('[data-pv-look-room-prints]');
    const seed = Array.from(root.dataset.handle || '').reduce((hash, ch) => (hash * 31 + ch.charCodeAt(0)) >>> 0, 0);
    background.src = rooms[seed % rooms.length];
    products.forEach(p => {
      const frame = document.createElement('a'); frame.className = 'pv-look-room-print';
      frame.href = `${rootUrl}products/${p.handle}`; frame.setAttribute('aria-label', p.title);
      const artVariant = p.variants.find(v => /50\s*[x×]\s*70/.test(v.option1) && /^(no frame|utan ram|sans cadre|ohne rahmen|sin marco|bez ramy|uden ramme|uten ramme)$/i.test(v.option2 || '') && v.featured_image);
      const art = artVariant?.featured_image;
      const src = new URL(art?.src || p.featured_image, location.origin); src.searchParams.set('width', '900');
      if (art?.width === 2048 && art?.height === 2048) {
        const ns = 'http://www.w3.org/2000/svg'; const svg = document.createElementNS(ns, 'svg'); const im = document.createElementNS(ns, 'image');
        svg.setAttribute('viewBox', '316 33 1418 1986'); svg.setAttribute('preserveAspectRatio', 'xMidYMid slice'); svg.setAttribute('aria-hidden', 'true');
        im.setAttribute('href', src.href); im.setAttribute('width', '2048'); im.setAttribute('height', '2048'); svg.append(im); frame.append(svg);
      } else { const im = document.createElement('img'); im.src = src.href; im.alt = ''; im.loading = 'lazy'; frame.append(im); }
      prints.append(frame);
    });

  }
  async function init(root) {
    if (root.dataset.pvLookReady) return;
    root.dataset.pvLookReady = 'true';
    const originalParent = root.parentNode;
    const originalNext = root.nextSibling;
    const desktop = matchMedia('(min-width: 990px)');
    const place = () => {
      const target = document.querySelector('.product-details > .group-block');
      if (desktop.matches && target) { target.after(root); root.classList.add('pv-look-inline'); }
      else {
        const about = document.querySelector('.pv-product-details-grid');
        const section = about?.closest('.shopify-section');
        if (section) section.before(root);
        else originalParent.insertBefore(root, originalNext?.parentNode === originalParent ? originalNext : null);
        root.classList.remove('pv-look-inline');
      }
    };
    place();
    desktop.addEventListener('change', place);
    try {
      const me = normalize(await load(`${rootUrl}products/${encodeURIComponent(root.dataset.handle)}.js`));
      if (!eligible(me)) return;
      const meta = metadata(me);
      const queryTags = [...new Set([
        ...meta.colours.slice(0, 3).map(c => `colour:${c}`),
        ...meta.styles.filter(s => s !== 'typography').slice(0, 2).map(s => `style:${s}`),
        ...(meta.typography ? ['style-abstract', 'style-illustration'] : ['subject-typography']),
        ...meta.subjects.filter(s => s !== 'typography').slice(0, 1).map(s => `subject:${s}`)
      ])].slice(0, 8);
      const lists = await Promise.allSettled(queryTags.map(t => load(`${rootUrl}collections/all/${encodeURIComponent(t.replace(/:/g, '-'))}?view=pvjson`)));
      const related = await load(`${rootUrl}recommendations/products.json?product_id=${me.id}&limit=10&intent=related`).catch(() => ({ products: [] }));
      const seen = new Set([me.handle]);
      const candidates = [...lists.flatMap(r => r.status === 'fulfilled' ? r.value.products || [] : []), ...related.products]
        .filter(p => { if (seen.has(p.handle) || !eligible(p) || key(p) === key(me)) return false; seen.add(p.handle); return true; })
        .sort((a, b) => affinity(me, b) - affinity(me, a) || a.handle.localeCompare(b.handle));
      const myVisual = await palette(me);
      const ranked = (await Promise.all(balanced(candidates, 36, p => p).map(async p => ({ p, visual: await palette(p) }))))
        .sort((a, b) => affinity(me, b.p) + harmony(myVisual, b.visual) - affinity(me, a.p) - harmony(myVisual, a.visual));
      const full = await Promise.allSettled(balanced(ranked.filter(item => compatible(myVisual, item.visual)), 14, item => item.p).map(async item => ({ ...item, p: normalize(await load(`${rootUrl}products/${item.p.handle}.js`)) })));
      const picked = select({ p: me, visual: myVisual }, full.filter(r => r.status === 'fulfilled' && eligible(r.value.p)).map(r => r.value));
      if (picked.length !== 2 || !root.isConnected) return;
      const all = [picked[0], me, picked[1]], row = root.querySelector('[data-pv-look-row]');
      const pct = Number(root.dataset.pct) || 15;
      const currency = window.Shopify?.currency?.active || 'SEK';
      const money = c => new Intl.NumberFormat(language, { style: 'currency', currency }).format(c / 100);
      row.replaceChildren();
      all.forEach(p => {
        const a = document.createElement('a'); a.href = `${rootUrl}products/${p.handle}`; a.className = 'pv-look-item';
        const img = document.createElement('img'); const src = new URL(p.featured_image, location.origin); src.searchParams.set('width', '480');
        img.src = src.href; img.alt = p.title; img.loading = 'lazy'; img.width = 480; img.height = 672;
        const title = document.createElement('span'); title.textContent = p.title; a.append(img, title); row.append(a);
      });
      const sizes = ['50x70 cm', '50x70 cm', '50x70 cm'];
      const frames = [...new Set(all[0].variants.filter(v => v.available && v.option1 === sizes[0]).map(v => v.option2))]
        .filter(f => all.every((p, i) => p.variants.some(v => v.available && v.option1 === sizes[i] && v.option2 === f)));
      if (!frames.length) return;
      const sum = Math.min(...frames.map(f => all.reduce((total, p, i) => total + p.variants.find(v => v.available && v.option1 === sizes[i] && v.option2 === f).price, 0)));
      [...row.children].forEach((a, i) => { const size = document.createElement('small'); size.textContent = sizes[i].replace('x', ' × '); a.append(size); });
      root.querySelector('[data-pv-look-deal]').textContent = money(Math.round(sum * (100 - pct) / 100));
      root.querySelector('[data-pv-look-was]').textContent = money(sum);
      const btn = root.querySelector('[data-pv-look-buy]');
      btn.onclick = () => window.PvBundleDrawer?.open({ name: root.dataset.name, pct, sizes: ['50x70', '50x70', '50x70'], products: all.map(p => ({ ...p, url: `${rootUrl}products/${p.handle}` })), trigger: btn });
      const visualsByHandle = new Map([[me.handle, myVisual], ...ranked.map(item => [item.p.handle, item.visual])]);
      scene(root, all, all.map(p => visualsByHandle.get(p.handle) || metadata(p)));
      root.hidden = false;
    } catch (_) { root.hidden = true; }
  }
  function start(scope) { scope.querySelectorAll('[data-pv-look]').forEach(init); }
  start(document);
  document.addEventListener('shopify:section:load', e => start(e.target));
})();

